/**
 * views/reporting/sections/KpiRow.jsx - the six KPI tiles.
 * Rendered by calling it from ReportingView.jsx with the values it reads,
 * so it renders the same DOM the view used to build inline.
 */
import { Explainer } from '../../../components/ui/Explainer';
import { fmt } from '../../../utils/format';
import { winRateColor } from '../reportingHelpers';
import { CARD } from '../reportingTokens';

export function renderKpiRow({
  kpiOpenValue,
  kpiSets,
  yearLabel,
  kpiWonValue,
  kpiLostValue,
  kpiWinMargin,
  kpiWonCogs,
  kpiWinRate,
  projectsThisYear,
  validProjects,
}) {
  return (
    <>
      {/* KPI ROW */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          {
            label: 'Open Value',
            value: fmt(kpiOpenValue) === '-' ? '$0' : fmt(kpiOpenValue),
            sub: `${kpiSets.open.length} open bids`,
            trend: yearLabel,
            icon: 'dollar',
            accent: '#1B5E20',
            explain: {
              title: 'Open Value',
              body: 'Sum of quoted price across every Open bid whose quote date falls in the selected year. Since 9/29/26 Open includes the bids that used to be Budget.',
              formula: "SUM(quoted_price)\n  WHERE status = 'Open'\n    AND quote_date is within selected year",
            },
          },
          {
            label: 'Won Value',
            value: fmt(kpiWonValue) === '-' ? '$0' : fmt(kpiWonValue),
            sub: `${kpiSets.won.length} deals closed`,
            trend: yearLabel,
            icon: 'trending',
            accent: '#1B5E20',
            explain: {
              title: 'Won Value',
              body: 'Sum of quoted price across every project marked WON whose quote date falls in the selected year. As an "open" project closes, its value moves out of Pipeline and into Won.',
              formula: "SUM(quoted_price)\n  WHERE status = 'Won'\n    AND quote_date is within selected year",
            },
          },
          {
            label: 'Lost Value',
            value: fmt(kpiLostValue) === '-' ? '$0' : fmt(kpiLostValue),
            sub: `${kpiSets.lost.length} deals lost`,
            trend: yearLabel,
            icon: 'trending',
            accent: '#B71C1C',
            explain: {
              title: 'Lost Value',
              body: 'Sum of quoted price across every project marked LOST whose quote date falls in the selected year.',
              formula: "SUM(quoted_price)\n  WHERE status = 'Lost'\n    AND quote_date is within selected year",
            },
          },
          {
            label: 'Win Margin',
            value: kpiWinMargin != null ? `${kpiWinMargin.toFixed(2)}%` : '-',
            sub: kpiWinMargin != null ? `${fmt(kpiWonValue - kpiWonCogs)} gross profit` : 'no won deals',
            trend: yearLabel,
            icon: 'trending',
            accent: '#1B5E20',
            explain: {
              title: 'Win Margin',
              body: "Gross margin across the year's WON deals: won sell minus won cost, divided by won sell. Cost is each deal's quoted COGS (quoted labor plus quoted expenses, or the fixed COGS figure when one is set on the project).",
              formula: "(won_sell − won_cogs) / won_sell × 100\n  WHERE status = 'Won'\n    AND quote_date is within selected year",
            },
          },
          {
            label: 'Win Rate',
            value: `${kpiWinRate}%`,
            sub: `${kpiSets.won.length}W · ${kpiSets.lost.length}L`,
            trend: yearLabel,
            icon: 'check',
            accent: winRateColor(parseFloat(kpiWinRate)),
            explain: {
              title: 'Win Rate',
              body: "Share of closed deals that were won. Open bids are excluded because they are not closed yet, so they don't affect win rate.",
              formula: 'won / (won + lost) × 100',
            },
          },
          {
            label: 'Total Bids',
            value: projectsThisYear.length,
            sub: yearLabel,
            trend: `${validProjects.length} all time (2012–present)`,
            icon: 'briefcase',
            accent: '#0D0D0D',
            explain: {
              title: 'Total Bids',
              body: 'Every opportunity and project in GlobalOps that names a customer and whose quote date falls in the selected year, regardless of status.',
              formula: 'COUNT(*) where quote_date is within selected year',
            },
          },
        ].map((kpi, i) => (
          <div
            key={i}
            style={{ ...CARD, cursor: 'default', transition: 'box-shadow 0.2s', overflow: 'visible' }}
            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)')}
            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 3,
                height: '100%',
                background: kpi.accent,
                borderRadius: '8px 0 0 8px',
              }}
            />
            <div style={{ paddingLeft: 8 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#5A5A5A',
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span>{kpi.label}</span>
                {kpi.explain && (
                  <Explainer title={kpi.explain.title} formula={kpi.explain.formula} align={i >= 3 ? 'right' : 'left'}>
                    {kpi.explain.body}
                  </Explainer>
                )}
              </div>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#0D0D0D', lineHeight: 1, marginBottom: 6 }}>{kpi.value}</div>
              <div style={{ fontSize: 11, color: '#888', fontWeight: 500 }}>{kpi.sub}</div>
              {kpi.trend && <div style={{ fontSize: 10, color: '#43A047', fontWeight: 600, marginTop: 4 }}>{kpi.trend}</div>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
